import { Injectable } from '@angular/core';
import { EMPTY, Observable, throwError } from 'rxjs';
import {
  AssistantKey,
  AssistantVersionKey,
  cancelTestRunV2$,
  ConfigVoiceConfig,
  deleteTestCases$,
  deleteTestSuite$,
  generateTestCases$,
  GenerateTestCasesResponse,
  getTestRunV2$,
  getTestSuite$,
  GetTestSuiteResponse,
  listTestCasesByAssistant$,
  ListTestCasesByAssistantResponse,
  listTestResults$,
  ListTestResultsResponse,
  listTestRuns$,
  ListTestRunsResponse,
  listTestSuites$,
  ListTestSuitesResponse,
  retryStuckTestResults$,
  RetryStuckTestResultsResponse,
  runSuccessEvaluation$,
  RunSuccessEvaluationResponse,
  runTestsV2$,
  RunTestsV2Response,
  SortDirection,
  TestCase,
  TestCaseKey,
  TestLLMOptions,
  TestResult,
  TestRun,
  TestSuite,
  TestType,
  upsertTestCases$,
  upsertTestSuite$,
  UpsertTestSuiteResponse,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { PagedRequestOptionsInterface } from '@vendasta/galaxy/table';
import { isResourceExhausted } from '../../core/utils/grpc-status.util';
import { PagedRequestOptions } from '@vendasta/protos/vendastatypes';
import { catchError, expand, map, reduce } from 'rxjs/operators';

// Default page size for fetching all test results. Kept small so a single
// page's response stays well under the 4 MiB gRPC message limit.
const TEST_RESULTS_PAGE_SIZE = 30;
// Fallback page size used when a page still exceeds the gRPC message limit at
// the default size and the gateway returns 429 (RESOURCE_EXHAUSTED).
const TEST_RESULTS_FALLBACK_PAGE_SIZE = 5;

function toProtoPagingOptions(options?: PagedRequestOptionsInterface): PagedRequestOptions {
  return {
    cursor: options?.cursor ?? '',
    pageSize: options?.pageSize ?? 0,
  };
}

export type SourceFilter =
  | { type: 'conversationId'; value: string }
  | { type: 'callRecordId'; value: string };

@Injectable({
  providedIn: 'root',
})
export class TestManagementService {
  upsertTestCases(assistantKey: Partial<AssistantKey>, cases: Partial<TestCase>[]): Observable<void> {
    return upsertTestCases$({ assistant: assistantKey, testCases: cases });
  }

  deleteTestCases(assistantKey: Partial<AssistantKey>, testCaseIDs?: string[]): Observable<void> {
    return deleteTestCases$({
      assistant: assistantKey,
      testCaseIds: testCaseIDs,
    });
  }

  listTestCasesByAssistant(
    assistantKey: Partial<AssistantKey>,
    pagingOptions: PagedRequestOptionsInterface,
    nameSearch: string,
    nameSortDirection: SortDirection,
    onlyEvaluators?: boolean,
  ): Observable<ListTestCasesByAssistantResponse> {
    return listTestCasesByAssistant$({
      searchTerm: nameSearch,
      sortOptions: {
        field: 'name',
        direction: nameSortDirection,
      },
      assistant: assistantKey,
      pagingOptions: toProtoPagingOptions(pagingOptions),
      onlyEvaluators: onlyEvaluators ?? false,
    });
  }

  getTestRunV2(testRunId: string, filter: { assistantKey?: Partial<AssistantKey>; testSuiteId?: string }): Observable<TestRun> {
    return getTestRunV2$({
      assistant: filter.assistantKey,
      id: testRunId,
      testSuiteId: filter.testSuiteId,
    }).pipe(map((response) => response?.testRun));
  }

  listTestResults(
    testRunId: string,
    filter: { assistantKey?: Partial<AssistantKey>; testSuiteId?: string },
    pagingOptions?: PagedRequestOptionsInterface,
  ): Observable<ListTestResultsResponse> {
    return listTestResults$({
      assistant: filter.assistantKey,
      testSuiteId: filter.testSuiteId,
      testRunId: testRunId,
      pagingOptions: toProtoPagingOptions(pagingOptions),
    });
  }

  fetchAllTestResults(
    testRunId: string,
    filter: { assistantKey?: Partial<AssistantKey>; testSuiteId?: string },
  ): Observable<TestResult[]> {
    return this.fetchResultPage(testRunId, filter, '', TEST_RESULTS_PAGE_SIZE).pipe(
      expand((page) =>
        page.response.pagingMetadata?.hasMore
          ? // Reuse this page's effective size for the next page: once a page
            // has degraded to the fallback size, stay there for the rest of the
            // pagination since the remaining results are likely just as large.
            this.fetchResultPage(testRunId, filter, page.response.pagingMetadata.nextCursor, page.pageSize)
          : EMPTY,
      ),
      reduce((acc: TestResult[], page) => acc.concat(page.response.testResults || []), []),
    );
  }

  // fetchResultPage fetches a single page of results. If the response exceeds
  // the gRPC message limit (gateway 429), it retries the same cursor at the
  // fallback page size. The VStore cursor encodes a position in the ordered
  // result stream and is independent of page size, so re-requesting the same
  // cursor with a smaller size resumes from the same spot without skipping or
  // duplicating results.
  private fetchResultPage(
    testRunId: string,
    filter: { assistantKey?: Partial<AssistantKey>; testSuiteId?: string },
    cursor: string,
    pageSize: number,
  ): Observable<{ response: ListTestResultsResponse; pageSize: number }> {
    return this.listTestResults(testRunId, filter, { pageSize, cursor }).pipe(
      map((response) => ({ response, pageSize })),
      catchError((err) => {
        if (pageSize > TEST_RESULTS_FALLBACK_PAGE_SIZE && isResourceExhausted(err)) {
          return this.listTestResults(testRunId, filter, {
            pageSize: TEST_RESULTS_FALLBACK_PAGE_SIZE,
            cursor,
          }).pipe(map((response) => ({ response, pageSize: TEST_RESULTS_FALLBACK_PAGE_SIZE })));
        }
        return throwError(() => err);
      }),
    );
  }

  cancelTestRun(
    testRunId: string,
    testRunType: { assistantKey?: Partial<AssistantKey>; testSuiteId?: string },
  ): Observable<void> {
    return cancelTestRunV2$({
      assistant: testRunType.assistantKey,
      id: testRunId,
      testSuiteId: testRunType.testSuiteId,
    });
  }

  retryStuckTestResults(
    testRunId: string,
    filter: { assistantKey?: Partial<AssistantKey>; testSuiteId?: string },
  ): Observable<RetryStuckTestResultsResponse> {
    return retryStuckTestResults$({
      assistant: filter.assistantKey,
      testRunId: testRunId,
      testSuiteId: filter.testSuiteId,
    });
  }

  // Test Suite methods
  listTestSuites(
    searchTerm?: string,
    tags?: string[],
    pagingOptions?: PagedRequestOptionsInterface,
  ): Observable<ListTestSuitesResponse> {
    if (!pagingOptions) {
      pagingOptions = { pageSize: 10, cursor: '' };
    }

    return listTestSuites$({
      searchTerm: searchTerm,
      tags: tags,
      pagingOptions: toProtoPagingOptions(pagingOptions),
    });
  }

  getTestSuite(id: string): Observable<GetTestSuiteResponse> {
    return getTestSuite$({ id: id });
  }

  upsertTestSuite(testSuite: Partial<TestSuite>): Observable<UpsertTestSuiteResponse> {
    return upsertTestSuite$({ testSuite: testSuite });
  }

  deleteTestSuite(id: string): Observable<void> {
    return deleteTestSuite$({ id: id });
  }

  // New listTestRuns that supports both assistant and suite filters
  listTestRuns(
    filter: { assistantKey?: Partial<AssistantKey>; testSuiteId?: string },
    pagingOptions?: PagedRequestOptionsInterface,
    onlySuccessEvaluations?: boolean,
    source?: SourceFilter,
  ): Observable<ListTestRunsResponse> {
    return listTestRuns$({
      assistant: filter.assistantKey,
      testSuiteId: filter.testSuiteId,
      pagingOptions: toProtoPagingOptions(pagingOptions),
      onlySuccessEvaluations: onlySuccessEvaluations ?? false,
      ...(source?.type === 'callRecordId' && { sourceCallRecordId: source.value }),
      ...(source?.type === 'conversationId' && { sourceConversationId: source.value }),
    });
  }

  generateTestCases(
    assistantKey: Partial<AssistantKey>,
    instructions: string,
    testType?: TestType,
  ): Observable<GenerateTestCasesResponse> {
    return generateTestCases$({
      assistant: assistantKey,
      instructions,
      testType: testType ?? 'TEST_TYPE_CHAT',
    });
  }

  runTestsV2(opts: RunTestsV2Options): Observable<RunTestsV2Response> {
    return runTestsV2$({
      assistant: opts.assistantKey,
      testSuiteId: opts.testSuiteId,
      testRunName: opts.testRunName,
      llmOptions: opts.llmOptions,
      voiceConfig: opts.voiceConfig,
      testCaseKeys: opts.testCaseKeys,
      iterations: opts.iterations,
      assistantVersionKey: opts.assistantVersionKey,
    });
  }

  runSuccessEvaluation(
    assistantKey: Partial<AssistantKey>,
    source: { conversationId: string } | { callRecordId: string },
  ): Observable<RunSuccessEvaluationResponse> {
    return runSuccessEvaluation$({
      assistantKey,
      ...('conversationId' in source
        ? { conversationId: source.conversationId }
        : { callRecordId: source.callRecordId }),
    });
  }
}

export interface RunTestsV2Options {
  assistantKey?: Partial<AssistantKey>;
  testSuiteId?: string;
  testRunName?: string;
  llmOptions?: Partial<TestLLMOptions>;
  voiceConfig?: Partial<ConfigVoiceConfig>;
  testCaseKeys?: TestCaseKey[];
  iterations?: number;
  /** Unset runs against the assistant's live configuration. */
  assistantVersionKey?: Partial<AssistantVersionKey>;
}
